'use client';
import React from 'react';
import dynamic from 'next/dynamic';
import { ProductGrid } from '../ProductGrid';
import { CategoryList } from '../CategoryList';
import { BlockProps } from './types';
import { useCatalogContext } from '../CatalogContext';
import { cn } from '@/core/utils/cn';
import { SlidersHorizontal, X } from 'lucide-react';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

// CartBlock/CheckoutBlock (courier widgets: InPost geowidget, ePaka, BLPaczka,
// Polkurier) only ever render on the cart/checkout pages, but were statically
// imported here — meaning every page using the Builder (including the
// homepage) shipped their JS too. Load them on demand instead.
const CartBlock = dynamic(() => import('./CartBlock').then(m => m.CartBlock), {
  loading: () => <div className="w-full min-h-[200px] animate-pulse bg-gray-50 rounded-2xl" />,
});
const CheckoutBlock = dynamic(() => import('./CheckoutBlock').then(m => m.CheckoutBlock), {
  loading: () => <div className="w-full min-h-[400px] animate-pulse bg-gray-50 rounded-2xl" />,
});
// Same for the catalog filters, the contact form and the newsletter box: SimpleBlocks also renders
// every product grid, so their static imports put them into every homepage's JavaScript. Still
// server-rendered (dynamic() keeps SSR), only their client code now loads with the pages using them.
const SidebarFilters = dynamic(() => import('@/modules/shop/components/catalog/sidebar-filters').then(m => m.SidebarFilters));
const ContactForm = dynamic(() => import('../ContactForm').then(m => m.ContactForm));
const Newsletter = dynamic(() => import('../Newsletter').then(m => m.Newsletter));

const DEMO_FILTERS = { query: '', minPrice: '', maxPrice: '', subcategoryId: null, facets: {} };
const DEMO_SUBCATEGORIES = [
  { id: 1, name: 'Podkategoria A', slug: 'a' },
  { id: 2, name: 'Podkategoria B', slug: 'b' },
];
const DEMO_FACETS = {
  total: 12,
  price: { min: 49, max: 399 },
  facets: [
    { key: 'rozmiar', values: ['XS', 'S', 'M', 'L', 'XL'].map((label, index) => ({ value: label.toLowerCase(), label, count: 8 - index })) },
    { key: '@brand', values: [{ value: 'nike', label: 'Nike', count: 7 }, { value: 'uniqlo', label: 'Uniqlo', count: 5 }] },
    { key: '@condition', values: [{ value: 'new', label: 'new', count: 8 }, { value: 'used', label: 'used', count: 4 }] },
  ],
};

function CatalogFiltersBlock({ isAdmin, currentStyles = {}, block }: { isAdmin?: boolean; currentStyles?: any; block?: any }) {
  const ctx = useCatalogContext();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const styleConfig = block?.settings?.filtersStyle || undefined;

  const customBgColor = currentStyles.backgroundColor;
  const customBorderColor = currentStyles.borderColor;
  const customBorderWidth = currentStyles.borderWidth;
  const customBorderRadius = currentStyles.borderRadius;
  const customTextColor = currentStyles.textColor || currentStyles.color;

  const inlineStyles: React.CSSProperties = {
    backgroundColor: customBgColor,
    borderColor: customBorderColor,
    borderWidth: customBorderWidth !== undefined ? `${customBorderWidth}px` : undefined,
    borderStyle: customBorderWidth ? 'solid' : undefined,
    borderRadius: customBorderRadius !== undefined ? `${customBorderRadius}px` : undefined,
    color: customTextColor,
  };

  const containerClass = cn(
    "p-5 transition-all duration-300",
    !customBgColor && "bg-white",
    !customBorderColor && !customBorderWidth && "border border-slate-100",
    customBorderRadius === undefined && "rounded-2xl",
    "shadow-sm"
  );

  // In admin editor there's no CatalogProvider — show a live-looking demo so the user
  // can see what the filters will look like on the actual category page.
  if (!ctx) {
    if (!isAdmin) return null;
    return (
      <div className="space-y-2" style={{ color: customTextColor }}>
        <div 
          className={cn(containerClass, "opacity-70 pointer-events-none select-none relative")}
          style={inlineStyles}
        >
          <div className="absolute top-2 right-2 bg-blue-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest z-10">Podgląd</div>
          <SidebarFilters
            initialFilters={DEMO_FILTERS}
            onFiltersChange={() => {}}
            subcategories={DEMO_SUBCATEGORIES}
            productCount={12}
            styleConfig={styleConfig}
            previewFacetData={DEMO_FACETS}
          />
        </div>
        <p className="text-[10px] opacity-60 text-center px-2">
          Filtry działają na żywo na stronie kategorii. Upewnij się, że blok <strong>Produkty</strong> ma ustawiony tryb <strong>„Bieżąca kategoria"</strong>.
        </p>
      </div>
    );
  }

  const activeCount = [
    ctx.filters.query,
    ctx.filters.minPrice,
    ctx.filters.maxPrice,
    ctx.filters.subcategoryId !== null,
  ].filter(Boolean).length;
  const productCountText = t('category.products_count', '{n} produktów')
    .replace('{n}', String(ctx.productCount));

  return (
    <>
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-current/15 bg-white shadow-sm text-sm font-semibold"
          style={{ color: customTextColor, borderColor: customBorderColor, backgroundColor: customBgColor }}
          aria-expanded={mobileOpen}
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 opacity-70" />
            {t('filters.title', 'Filtry')}
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-[9px] font-black">
                {activeCount}
              </span>
            )}
          </span>
          <span className="text-xs opacity-60">{productCountText}</span>
        </button>

        <div
          className={cn(
            'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden',
            mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
          )}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-80 max-w-[88vw] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out lg:hidden',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
          role="dialog"
          aria-modal="true"
          aria-label={t('filters.title', 'Filtry')}
        >
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <span className="flex items-center gap-2 font-black text-sm uppercase tracking-wider text-slate-800">
              <SlidersHorizontal className="w-4 h-4" />
              {t('filters.title', 'Filtry')}
            </span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              aria-label={t('common.close', 'Zamknij')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5" style={{ color: customTextColor }}>
            <SidebarFilters
              initialFilters={ctx.filters}
              onFiltersChange={ctx.setFilters}
              subcategories={ctx.subcategories}
              productCount={ctx.productCount}
              styleConfig={styleConfig}
              hideHeader
              categoryId={ctx.categoryId}
              onFacetsChange={ctx.setFacets}
            />
          </div>
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="w-full py-3 rounded-xl bg-slate-900 text-white font-bold text-sm"
            >
              {productCountText}
            </button>
          </div>
        </div>
      </div>

      <div className={cn(containerClass, 'hidden lg:block')} style={inlineStyles}>
        <SidebarFilters
          initialFilters={ctx.filters}
          onFiltersChange={ctx.setFilters}
          subcategories={ctx.subcategories}
          productCount={ctx.productCount}
          styleConfig={styleConfig}
          categoryId={ctx.categoryId}
          onFacetsChange={ctx.setFacets}
        />
      </div>
    </>
  );
}

export function SimpleBlocks({ block, currentStyles, viewport, isAdmin, category, initialData, isFirstBlock }: BlockProps) {
  if (block.type === 'cart') {
    return (
      <div className="w-full relative z-10 block-inner-content">
        <CartBlock isAdmin={isAdmin} settings={block.settings} viewport={viewport} styles={block.styles} />
      </div>
    );
  }
  if (block.type === 'checkout') {
    return (
      <div className="w-full relative z-10 block-inner-content">
        <CheckoutBlock isAdmin={isAdmin} settings={block.settings} viewport={viewport} styles={block.styles} />
      </div>
    );
  }
  if (block.type === 'products') {
    return (
      <div className="w-full relative z-10 block-inner-content">
        <ProductGrid {...(block.settings || {})} blockId={block.id} customStyles={currentStyles} editorViewport={isAdmin ? viewport : undefined} activeCategory={category} initialProducts={!isAdmin ? initialData : undefined} isFirstBlock={isFirstBlock} />
      </div>
    );
  }
  if (block.type === 'categories') {
    return (
      <div className="w-full relative z-10 block-inner-content flex justify-center">
        <CategoryList settings={block.settings} initialCategories={!isAdmin ? initialData : undefined} styles={block.styles} viewport={viewport} />
      </div>
    );
  }
  if (block.type === 'contact-form') {
    return (
      <div className="w-full relative z-10 block-inner-content">
        <ContactForm settings={block.settings} styles={block.styles} viewport={viewport} />
      </div>
    );
  }
  if (block.type === 'newsletter') {
    return (
      <div className="w-full relative z-10 block-inner-content">
        <Newsletter settings={block.settings} styles={block.styles} viewport={viewport} />
      </div>
    );
  }
  if (block.type === 'catalog-filters') {
    return <CatalogFiltersBlock isAdmin={isAdmin} currentStyles={currentStyles} block={block} />;
  }
  return null;
}
